import message from 'antd/lib/message';

import {
  buildQuery,
  mapCatchData,
  reportOptionChange,
  reportPeriodChange,
  storeHCParams
} from './helpers';
import { ENDPOINTS, ERR_MESSAGE_DURATION } from 'other/config';
import {
  ECatchesActions,
  fetchCatchDataSet,
  fetchOptionsSet,
  fetchVizSet,
  INITIAL_SETTINGS
} from './catchesConstants';
import { getDefaultPeriod } from './catchesInitialState';
import { http } from 'services/http';
import { isWindyLayer } from 'pages/Map/utils/helpers';
import { MultiSelect } from 'components/common/MultiSelect/MultiSelect';
import { resetLayersAction } from '../mapOptions/mapOptionsActions';
import { stringComparator } from 'other/helpers';
import { VESSEL_TYPE_LIST } from 'pages/Map/controls/MapControlPane/VesselMenu/vesselTypes';

import { TAction } from '../../_utils/reducerCreator';
import {
  TCatchesPeriod,
  TCatchFilterSettings,
  THistoricalCatchRecord,
  TIdLocationDict,
  TSelectOption,
  TSpecieSimple,
  TVesselSimple,
  TVesselTripResponse
} from 'types';
import { TCatchesState } from './catchesModel';
import { THttpResponse } from 'services/HttpClass';
import { TState } from '../../appStateModel';

/**/
let historyController;
let optionsController;
let pointsController;

/**
 *
 */
export function changeFilterAction(value: Partial<TCatchFilterSettings>) {
  return (dispatch, getState) => {
    const { catches, dictionaries, mapOptions } = getState() as TState;
    const equipment = [
      MultiSelect.getAllOption('All'),
      ...dictionaries.vesselGearTypes
    ];

    const options = { ...catches.options, equipment };
    const settings = { ...catches.settings, ...value };

    reportOptionChange(value, options);
    storeHCParams({
      options: {
        equipment,
        ...catches.settings,
        ...value
      }
    });

    dispatch({
      type: ECatchesActions.CHANGE_FILTER,
      payload: { settings }
    });

    dispatch(fetchCatchVizAction());
    dispatch(fetchCatchDataAction(mapOptions.zoom));
  };
}

/**
 *
 */
export function changePeriodAction(value: Partial<TCatchesPeriod>) {
  return (dispatch, getState) => {
    const { catches, mapOptions } = getState() as TState;
    const period = { ...catches.period, ...value };

    reportPeriodChange(value);
    storeHCParams({ period });

    dispatch({
      type: ECatchesActions.CHANGE_PERIOD,
      payload: { period }
    });

    dispatch(fetchOptionsAction());
    dispatch(fetchCatchVizAction());
    dispatch(fetchCatchDataAction(mapOptions.zoom));
  };
}

/**
 *
 */
export function addCardAction() {
  return (dispatch, getState) => {
    const { catches } = getState() as TState;
    dispatch({
      type: ECatchesActions.ADD_CARD,
      payload: { cardCount: catches.cardCount + 1 }
    });
  };
}

/**
 *
 */
export function removeCardsAction(): TAction<TCatchesState, ECatchesActions> {
  return {
    type: ECatchesActions.REMOVE_CARDS,
    payload: { cardCount: 0 }
  };
}

/**
 *
 */
export function openPanelAction(
  shouldOpen: boolean
): TAction<TCatchesState, ECatchesActions> {
  return {
    type: ECatchesActions.OPEN_PANEL,
    payload: { isPanelExpanded: shouldOpen }
  };
}

/**
 *
 */
export function setCatchModeAction(isCatchMode: boolean) {
  return (dispatch, getState) => {
    const { mapOptions } = getState() as TState;
    storeHCParams({ isCatchMode });

    if (isWindyLayer(mapOptions.layer)) {
      dispatch(resetLayersAction());

      message.warning(
        {
          className: 'LocationsUpdate__message',
          content: 'Weather layers are not supported with Catch feature.'
        },
        ERR_MESSAGE_DURATION
      );
    }

    dispatch({
      type: ECatchesActions.SET_CATCH_MODE,
      payload: { isCatchMode }
    });
  };
}

/**
 *
 */
export function resetFiltersAction() {
  storeHCParams({ options: INITIAL_SETTINGS });
  return (dispatch, getState) => {
    dispatch({
      type: ECatchesActions.RESET_FILTERS,
      payload: {
        period: getDefaultPeriod(),
        settings: INITIAL_SETTINGS
      }
    });

    dispatch(fetchOptionsAction());
    dispatch(fetchCatchVizAction());
    dispatch(fetchCatchDataAction(getState().mapOptions.zoom));
  };
}

/**
 *
 */
export function fetchCatchDataAction(zoom: number) {
  return (dispatch, getState) => {
    const { catches } = getState() as TState;
    const { period, settings } = catches;
    pointsController?.abort();

    if (period.years.length === 0) {
      return dispatch(
        fetchCatchDataSet.success({
          activities: [],
          clusters: []
        })
      );
    }

    pointsController = new AbortController();
    dispatch(fetchCatchDataSet.request());

    http
      .send({
        method: 'GET',
        signal: pointsController.signal,
        url:
          ENDPOINTS.VESSEL_TRIP + buildQuery(settings, period) + `&zoom=${zoom}`
      })
      .then(({ data }: THttpResponse<TVesselTripResponse>) =>
        dispatch(fetchCatchDataSet.success(data))
      )
      .catch((e) => dispatch(fetchCatchDataSet.error(e)));
  };
}

/**
 *
 */
export function fetchCatchVizAction() {
  return (dispatch, getState) => {
    const { catches } = getState() as TState;
    const { period, settings } = catches;
    historyController?.abort();

    if (period.years.length === 0) {
      return dispatch(fetchVizSet.success({ catchVisualization: [] }));
    }

    historyController = new AbortController();
    dispatch(fetchVizSet.request());
    const _period = {
      end: 365,
      start: 1,
      years: period.years
    };

    http
      .send({
        method: 'GET',
        signal: historyController.signal,
        url: ENDPOINTS.VESSEL_TRIP_HISTORY + buildQuery(settings, _period)
      })
      .then(({ data }: THttpResponse<THistoricalCatchRecord[]>) =>
        dispatch(
          fetchVizSet.success({
            catchVisualization: mapCatchData(data, period.years)
          })
        )
      )
      .catch((e) => dispatch(fetchVizSet.error(e)));
  };
}

/**
 *
 */
export function fetchOptionsAction() {
  return (dispatch, getState) => {
    const { catches } = getState() as TState;

    if (catches.period.years.length === 0) {
      return dispatch(
        fetchOptionsSet.success({
          options: {
            species: [],
            types: [],
            vessels: []
          }
        })
      );
    }

    dispatch(fetchOptionsSet.request());

    const query = buildQuery(null, catches.period);
    optionsController?.abort();
    optionsController = new AbortController();

    Promise.all([fetchSpecies(query), fetchTypes(query), fetchVessels(query)])
      .then(([species, types, vessels]) =>
        dispatch(
          fetchOptionsSet.success({
            options: {
              species: [MultiSelect.getAllOption('All'), ...species],
              types: [MultiSelect.getAllOption('All'), ...types],
              vessels: [MultiSelect.getAllOption('All'), ...vessels]
            }
          })
        )
      )
      .catch((e) => dispatch(fetchOptionsSet.error(e)));
  };
}

/**
 *
 */
function fetchSpecies(query: string): Promise<TSelectOption<number>[]> {
  return http
    .send({
      method: 'GET',
      signal: optionsController.signal,
      url: ENDPOINTS.VESSEL_TRIP_SPECIES + query
    })
    .then(({ data }: THttpResponse<TSpecieSimple[]>) =>
      data
        ?.map(
          ({ id, value }): TSelectOption<number> => ({
            label: value.en_GB,
            value: id
          })
        )
        .sort(({ label: a }, { label: b }) => stringComparator(a, b))
    );
}

/**
 *
 */
function fetchTypes(query: string): Promise<TSelectOption<number>[]> {
  return http
    .send({
      method: 'GET',
      signal: optionsController.signal,
      url: ENDPOINTS.VESSEL_TRIP_TYPES + query
    })
    .then(({ data }: THttpResponse<TIdLocationDict[]>) => {
      const types = (data || []).map(({ id }: TIdLocationDict): number => id);
      return VESSEL_TYPE_LIST.filter(({ value }: TSelectOption<number>) =>
        types.includes(value)
      );
    });
}

/**
 *
 */
function fetchVessels(query: string): Promise<TSelectOption<number>[]> {
  return http
    .send({
      method: 'GET',
      signal: optionsController.signal,
      url: ENDPOINTS.VESSEL_TRIP_VESSELS + query
    })
    .then(({ data }: THttpResponse<TVesselSimple[]>) =>
      data
        .sort(({ name: a }, { name: b }) => stringComparator(a, b))
        .map(
          ({ id, name }): TSelectOption<number> => ({
            label: name,
            value: id
          })
        )
    );
}
